import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  GitCompare,
  CheckCircle,
  HelpCircle,
  BookOpen,
  Sparkles,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';
import { fireEvent } from '../utils/funnelEvents';

const CANONICAL = 'https://www.getsortio.com/hazel-vs-sortio';
const LAST_UPDATED = '2026-06-09';

const TABLE = {
  headers: ['', 'Hazel', 'Sortio'],
  rows: [
    ['Approach', 'Deterministic rules you write and maintain', 'Plain-English AI prompts, plus an AI Rule Builder for deterministic rules'],
    ['Platform', 'macOS only', 'macOS and Windows'],
    ['Reads document contents', 'Rule conditions can match text in files, brittle on messy real-world documents', 'Yes for text-based formats (PDF text layer, Word, Excel, PowerPoint, text) when content sorting is enabled'],
    ['OCR for image-only scans', 'No (relies on an existing text layer or third-party OCR)', 'No (image-only scans sort by filename and metadata)'],
    ['Preview before apply', 'No dry-run preview of a full rule pass', 'Yes, every sort shows the complete plan first'],
    ['Undo', 'Limited revert', 'Full one-click undo per run, with sort history'],
    ['Watch folders', 'Yes', 'Yes (Pro)'],
    ['macOS automation hooks', 'Deep: AppleScript, Automator, shell scripts', 'Apple Shortcuts and Quick Action integrations'],
    ['Languages', 'English', 'English, Chinese (Simplified and Traditional), Korean, German, Spanish'],
    ['Price', '$42 one-time license', 'Free tier; Pro $14.99/mo or $99/yr'],
    ['Best for', 'Rule engineers with stable, metadata-shaped patterns, all-Mac', 'Messy folders, unhelpful filenames, content-dependent routing, mixed Mac and Windows'],
  ],
};

const FAQ = [
  {
    question: 'Is Sortio a replacement for Hazel?',
    answer:
      'For most workflows, yes: Sortio covers folder watching, renaming, and routing, and adds AI sorting for the files rules cannot describe. The exception is deep AppleScript or shell-script automation chains, where Hazel\'s hooks go further. Many users run both: Hazel for the legacy rules that already work, Sortio for everything content-shaped.',
  },
  {
    question: 'Can I import my Hazel rules into Sortio?',
    answer:
      'There is no direct import. The practical path is the migration guide: describe each Hazel rule in plain English to Sortio\'s AI Rule Builder and it generates the equivalent rule. Pure metadata rules (extension, name pattern, date) translate one-to-one. Content-matching rules are usually better replaced by an AI sort prompt, since that is exactly the case where Hazel rules tend to be brittle.',
  },
  {
    question: 'Is Hazel still maintained?',
    answer:
      'Yes. Noodlesoft continues to ship updates and Hazel remains a solid, actively maintained product. The honest critique is not abandonment; it is that the rule engine has not evolved much for content-aware sorting, so messy PDFs and inconsistent filenames stay hard.',
  },
  {
    question: 'Which is cheaper, Hazel or Sortio?',
    answer:
      'Hazel, if rules cover everything you need: $42 once versus a Sortio Pro subscription at $14.99/month or $99/year. Sortio\'s free tier (a one-time AI trial allowance plus unlimited rule-based sorting) costs nothing, so the real comparison is Hazel\'s license against Sortio Pro, and what you are paying for in Pro is the AI sorting and watch folders.',
  },
  {
    question: 'Does either tool OCR scanned documents?',
    answer:
      'Neither has built-in OCR. Hazel can match content in PDFs that already have a text layer; Sortio reads text-based formats when content sorting is enabled. For image-only scans, run an OCR tool first (OCRmyPDF is a common pick), then either tool can work with the text layer it produces.',
  },
  {
    question: 'Does Hazel run on Windows?',
    answer:
      'No. Hazel is macOS-only and Noodlesoft has not announced a Windows version. If that is your actual question, see our Hazel for Windows page: Sortio runs the same app on Windows and macOS, and File Juggler and DropIt are the Windows-native rule-based options.',
  },
  {
    question: 'Does Sortio require an internet connection?',
    answer:
      'AI sorting uses Sortio\'s hosted AI by default, which requires a connection; hosted content is retained up to 30 days and then purged. You can instead use your own OpenAI or Anthropic API key, or run fully local with Ollama, which also makes offline sorting work.',
  },
];

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Hazel vs Sortio: Rules vs AI File Sorting, Compared Honestly',
  description:
    'Hazel is the deterministic Mac rule engine with a one-time license. Sortio is the AI organizer with prompts, content reading, and Windows support. Where each wins.',
  url: CANONICAL,
  datePublished: LAST_UPDATED,
  dateModified: LAST_UPDATED,
  about: {
    '@type': 'SoftwareApplication',
    name: 'Sortio',
    url: 'https://www.getsortio.com',
  },
  publisher: {
    '@type': 'Organization',
    name: 'CMG Labs LLC',
    url: 'https://www.getsortio.com',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
    { '@type': 'ListItem', position: 2, name: 'Compare', item: 'https://www.getsortio.com/compare' },
    { '@type': 'ListItem', position: 3, name: 'Hazel vs Sortio', item: CANONICAL },
  ],
};

const SECTIONS = [
  {
    heading: 'The short version',
    body: [
      'Hazel and Sortio solve the same problem with opposite philosophies. Hazel is a rule engine: you tell it exactly what to match and exactly what to do, and it does that forever, deterministically, for a $42 one-time license. Sortio is an AI organizer: you describe the outcome in plain English and a language model decides where each file belongs, with a rule builder for the patterns you want to make permanent.',
      'If your filing logic fits in metadata (extension, name pattern, date, source app) and you live entirely on a Mac, Hazel is hard to beat on price and reliability. If your folders are full of files whose names tell you nothing, or you need the same workflow on Windows, that is the gap Sortio exists to fill.',
    ],
  },
  {
    heading: 'Where Hazel wins',
    body: [
      'Price over time. $42 once versus a subscription. If you set up rules in 2020 and they still run today, Hazel has cost you pennies a month for genuinely useful automation.',
      'Determinism. A Hazel rule does the same thing every time. There is no model in the loop, no AI usage, and no judgment call. For compliance-flavored workflows where "the file named X always goes to Y" is the whole requirement, deterministic rules are the right tool, which is also why Sortio includes its own rule engine.',
      'Depth of macOS integration. AppleScript, Automator, shell script hooks, Finder tags, Spotlight conditions, nested rule logic. Long-time Hazel users have built automation chains Sortio does not try to replicate.',
    ],
  },
  {
    heading: 'Where Sortio wins',
    body: [
      'Files that resist rules. The most-cited Hazel frustration is content matching on real-world PDFs: a rule reads the account number on one bank statement and misses it on the next from the same bank, because the underlying text is inconsistent. Sortio sends the document text to a language model, which is robust to that noise; it reads what the document is rather than pattern-matching a string position.',
      'No rule authoring. "Sort these 800 downloads by what they are, put receipts in Finance/2026, installers in the trash list, and screenshots by month" is one sentence in Sortio. The equivalent Hazel setup is an afternoon of rule building, and it still misses the files that do not match.',
      'Safety on big moves. Sortio shows the complete plan, every rename and destination, before anything happens, and a sort can be undone in one click. Hazel acts file-by-file as rules fire, and unwinding a misconfigured rule that ran over a watched folder is manual work.',
      'Cross-platform. Sortio runs the same app, prompts, and rules on macOS and Windows. Hazel is Mac-only, full stop. For mixed-OS households or teams, that decides it.',
    ],
  },
  {
    heading: 'The pricing math, honestly',
    body: [
      'Over five years, Hazel costs $42 and Sortio Pro annual costs about $495. If a rule engine covers 100% of your needs, buy Hazel; the subscription only earns its keep if the AI is doing work rules cannot. The way to find out costs nothing: Sortio\'s free tier includes a one-time AI trial allowance and unlimited rule-based sorting, which is enough to run a real test on your worst folder before any money changes hands.',
      'A fair middle path many users land on: keep Hazel for the legacy rules that already work, and use Sortio for the backlog cleanups and content-dependent routing Hazel was never going to handle. The two coexist fine, and we maintain a guide for exactly that setup.',
    ],
  },
];

const HazelVsSortioPage = () => {
  const downloadClick = (ctaId) => () =>
    fireEvent('cta_click', { cta_id: ctaId, page: '/hazel-vs-sortio', target: '/download' });

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="Hazel vs Sortio (2026): Rules vs AI File Sorting Compared"
        description="Hazel vs Sortio head to head: deterministic Mac rules and a $42 license versus AI prompts that read document contents on Mac and Windows. Honest table, pricing math, and FAQ."
        canonical={CANONICAL}
        schema={[articleSchema, faqSchema, breadcrumbSchema]}
        keywords="hazel vs sortio, sortio vs hazel, hazel comparison, hazel ai alternative"
        lastModified={LAST_UPDATED}
      />

      <div className="container mx-auto px-6 max-w-4xl py-16 lg:py-24 relative z-10">
        <Breadcrumbs items={[{ label: 'Compare', href: '/compare' }, { label: 'Hazel vs Sortio', href: null }]} />

        <div className="mb-10">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <GitCompare className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-semibold mb-6 text-apple-50">
            Hazel vs Sortio: rules vs AI, head to head
          </h1>
          <p className="text-apple-300 text-lg leading-relaxed">
            Hazel is the deterministic Mac rule engine that has carried power users since 2006. Sortio is
            the AI organizer that reads files and takes prompts instead of rules. Here is the honest
            comparison, including where Hazel is still the right buy.
          </p>
          <p className="text-apple-400 text-sm mt-4">Last updated {new Date(`${LAST_UPDATED}T00:00:00`).toLocaleDateString()}</p>
        </div>

        {/* Comparison table */}
        <section className="mb-14">
          <h2 className="text-2xl font-semibold text-apple-50 mb-6">At a glance</h2>
          <div className="overflow-x-auto rounded-2xl border border-apple-600/30 bg-apple-700/20">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-apple-600/30">
                  {TABLE.headers.map((h, i) => (
                    <th key={i} className={`px-4 py-3 text-left font-medium ${i === 0 ? 'text-apple-300' : 'text-apple-50'}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TABLE.rows.map((row, r) => (
                  <tr key={r} className="border-b border-apple-600/20 last:border-0">
                    {row.map((cell, c) => (
                      <td key={c} className={`px-4 py-3 align-top ${c === 0 ? 'text-apple-400 font-medium' : 'text-apple-200'}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Sections */}
        <section className="mb-14 space-y-10">
          {SECTIONS.map((s, i) => (
            <div key={i}>
              <h2 className="text-2xl font-semibold text-apple-50 mb-4">{s.heading}</h2>
              <div className="space-y-4 text-apple-200 leading-relaxed">
                {s.body.map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* Verdict */}
        <section className="mb-14">
          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-8">
            <h2 className="text-2xl font-semibold text-apple-50 mb-4 flex items-center">
              <CheckCircle className="text-sortio-primary mr-3" size={24} />
              Which one should you pick?
            </h2>
            <div className="space-y-3 text-apple-200 leading-relaxed">
              <p>
                Pick Hazel if: you are all-Mac, your sorting logic is stable and metadata-shaped, you
                enjoy building rules, and you want to pay once.
              </p>
              <p>
                Pick Sortio if: your filenames are unhelpful, the routing depends on what documents are
                about, you want a preview and undo on every run, or you need the same workflow on Windows
                and macOS.
              </p>
              <p>
                Genuinely unsure? The test costs nothing: download Sortio, point it at the folder Hazel
                rules have never managed to tame, and look at the preview it proposes.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14">
          <h2 className="text-2xl font-semibold text-apple-50 mb-6 flex items-center">
            <HelpCircle className="text-sortio-primary mr-3" size={24} />
            Frequently asked questions
          </h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Keep reading */}
        <section className="mb-14">
          <h2 className="text-2xl font-semibold text-apple-50 mb-6 flex items-center">
            <BookOpen className="text-sortio-primary mr-3" size={24} />
            Keep reading
          </h2>
          <div className="grid gap-3">
            {[
              { label: 'Hazel alternatives for Mac: the full roundup', href: '/compare/hazel-alternative-mac' },
              { label: 'Hazel for Windows: what to use instead', href: '/hazel-for-windows' },
              { label: 'Migrate your Hazel rules to Sortio', href: '/guides/migrate-from-hazel-to-sortio' },
              { label: 'Use Sortio and Hazel together', href: '/guides/sortio-plus-hazel-workflow' },
              { label: 'Why Hazel content matching breaks on real-world PDFs', href: '/blog/hazel-content-matching-pdfs' },
            ].map((l) => (
              <Link
                key={l.href}
                to={l.href}
                className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
              >
                <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">{l.label}</span>
                <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section>
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Try Sortio for free</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier includes a one-time AI trial allowance and unlimited rule-based sorting. Enough to
              test on the folder your Hazel rules never tamed.
            </p>
            <Link
              to="/download"
              onClick={downloadClick('hazel_vs_sortio_footer')}
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HazelVsSortioPage;
